import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Body1 } from "@activecollab/components";
import classnames from "classnames";
import { List } from "immutable";
import moment from "moment";
import PropTypes from "prop-types";
import UserLink from "~/components/UserLink/UserLink";
import CommentFormContainer from "./CommentFormContainer.jsx";
import { CommentListItemWrapper } from "./CommentListItemWrapper.jsx";
import CommentListTop from "./CommentListTop.jsx";
import LoadMorePagination from "../component.load_more_pagination.jsx";
import WhoIsWriting from "../WhoIsWriting.jsx";

const CommentList = ({
  title,
  items,
  totalItems,
  loggedUserId,
  isFetchingComments,
  userIds,
  parentType,
  parentId,
  editCommentId,
  projectId,
  addComment,
  openForm,
  fetchComments,
  trashComment,
  closeForm,
  itemsPerPage,
  editComment,
}) => {
  const ref = useRef(null);
  const containerPresentedTimestamp = useMemo(() => moment().unix(), []);

  const { t } = useTranslation();

  const hideCommentForm = useCallback(() => {
    typeof closeForm === "function" && closeForm(editCommentId);
    if (editCommentId === 0) {
      ref?.current?.focus({ preventScroll: true });
    }
  }, [closeForm, editCommentId]);

  const handleAddComment = useCallback(
    (id, attributes) => {
      if (attributes && attributes.body && attributes.body.length) {
        hideCommentForm();
        typeof addComment === "function" &&
          addComment(id, {
            ...attributes,
            created_by_id: loggedUserId,
          });
      }
    },
    [loggedUserId, addComment, hideCommentForm]
  );

  const handleEditComment = useCallback(
    (id, attributes) => {
      if (id && attributes && attributes.body && attributes.body.length) {
        hideCommentForm();
        typeof editComment === "function" && editComment(id, { ...attributes });
      }
    },
    [hideCommentForm, editComment]
  );

  const showCommentForm = useCallback(
    (id) => {
      typeof openForm === "function" && openForm(id);
    },
    [openForm]
  );

  const showAddNewCommentForm = useCallback(() => {
    showCommentForm(0);
  }, [showCommentForm]);

  const handleFetchMore = useCallback(() => {
    typeof fetchComments === "function" && fetchComments(Math.floor(items.size / itemsPerPage) + 1);
  }, [items, itemsPerPage, fetchComments]);

  const handleMoveToTrash = useCallback(
    (id) => {
      typeof trashComment === "function" && trashComment(id);
    },
    [trashComment]
  );

  return (
    <div
      className="comments_list tw-relative"
      ref={ref}
      tabIndex="0"
    >
      <div className="tw-mb-2 tw-flex tw-items-center">
        <Body1 weight="bold">{title ? title : t("Comments List")}</Body1>
        <WhoIsWriting
          context={`comment-${parentType}-${parentId}`}
          className="who_is_writing_text tw-ml-2"
        />
      </div>
      {editCommentId === 0 && (
        <CommentFormContainer
          submitLabel={t("Add Comment")}
          onSubmit={handleAddComment}
          onCancel={hideCommentForm}
          createdById={loggedUserId}
          userIds={userIds}
          parentType={parentType}
          parentId={parentId}
          projectId={projectId}
        />
      )}
      {editCommentId !== 0 && (
        <>
          <div className="object_comments_form print_hide">
            <UserLink
              hideName
              id={loggedUserId}
              size={32}
              className="object_comments_form_avatar"
              context="comment - write a comment"
            />
            <div
              tabIndex={0}
              className="object_comments_faux_controls"
              data-qa-id="open-comment-form"
              onClick={showAddNewCommentForm}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  event.stopPropagation();

                  showAddNewCommentForm();
                }
              }}
            >
              {t("Write a comment")}...
            </div>
          </div>
        </>
      )}
      <CommentListTop
        parentType={parentType}
        parentId={parentId}
      />
      <div
        className={classnames(`object_comments_container`, {
          has_comments: !!items.size,
        })}
      >
        {items.map((itemId, index) => {
          return (
            <CommentListItemWrapper
              key={itemId}
              id={itemId}
              editMode={editCommentId === itemId}
              onSubmit={handleEditComment}
              onCancel={hideCommentForm}
              userIds={userIds}
              parentType={parentType}
              parentId={parentId}
              projectId={projectId}
              orderNumber={totalItems - index}
              onEdit={showCommentForm}
              onDelete={handleMoveToTrash}
              containerTimestamp={containerPresentedTimestamp}
            />
          );
        })}
      </div>
      {items.size < totalItems && (
        <LoadMorePagination
          title={t("Load older comments")}
          fetchMore={handleFetchMore}
          isLoading={isFetchingComments}
        />
      )}
    </div>
  );
};

CommentList.propTypes = {
  items: PropTypes.instanceOf(List).isRequired,
  loggedUserId: PropTypes.number.isRequired,
  title: PropTypes.string,
  itemsPerPage: PropTypes.number,
  parentType: PropTypes.string.isRequired,
  parentId: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  totalItems: PropTypes.number,
  editCommentId: PropTypes.number,
  isFetchingComments: PropTypes.bool,
  addComment: PropTypes.func,
  editComment: PropTypes.func,
  trashComment: PropTypes.func,
  fetchComments: PropTypes.func,
  openForm: PropTypes.func,
  closeForm: PropTypes.func,
  userIds: PropTypes.object,
};

CommentList.defaultProps = {
  itemsPerPage: 30,
  totalItems: 0,
  isFetchingComments: false,
};

export default CommentList;
